import StateObserver, { user } from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  FriendshipRequest,
  PlayerProfile,
} from 'src/replicant/friends/friends.ruleset';
import { track } from 'src/lib/analytics/utils';
import { FriendRequestSourceFeature } from './friends.analytics.schema';
import {
  getBuruFriendsCount,
  getFriendshipRequestMethod,
} from 'src/replicant/friends/friends.getters';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { GCInstant } from 'src/lib/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import { fetchFriendList, fetchFullUserProfile } from './friends.utils';
import { openProfile } from '../profile/profile.sequence';
import { refreshExternalFriends } from '../social/social.utils';

/**
 * Send a friend request to the given user
 */
export async function sendAddFriendRequest(
  player: PlayerProfile,
  opts: {
    sourceFeature: FriendRequestSourceFeature;
    originFeature?: string;
    originSubFeature?: string;
    skipConfirmation?: boolean;
  },
): Promise<boolean> {
  // Ask user for confirmation, unless the caller sends directly (e.g. the
  // You-May-Know suggestions section, where the "Add" button is the
  // confirmation).
  const confirmed = opts.skipConfirmation
    ? true
    : await openPopupPromise('popupConfirmFriendRequest', {
        player,
      });

  // If user confirmed, send request
  if (confirmed) {
    await StateObserver.invoke.createFriendshipRequest({
      target: player,
      sourceFeature: opts.sourceFeature,
      originFeature: opts.originFeature,
      originSubFeature: opts.originSubFeature,
      method: 'request',
    });

    // Send analytics event
    track('FriendRequestSend', {
      method: 'request',
      receiverID: player.id,
      friendCount: getBuruFriendsCount(user()),
      originFeature: opts.originFeature,
      originSubFeature: opts.originSubFeature,
      friendRequestReceiverID: player.id,
    });
  }

  return confirmed;
}

/**
 * Accept a friend request
 */
export async function acceptFriendRequest(
  request: FriendshipRequest,
  origin: 'launch' | 'hud',
) {
  track('FriendConfirmPopupOpen', {
    origin,
    originFeature: request.originFeature,
    originSubFeature: request.originSubFeature,
    friendRequestSenderID: request.owner.id,
  });

  // Ask user for confirmation
  const result = await openPopupPromise('popupFriendsConfirmation', {
    player: request.owner,
    prompt: 'accept',
  });

  track('FriendConfirmAction', {
    action: result,
    prompt: 'accept',
    senderID: request.owner.id,
    friendCount: getBuruFriendsCount(user()),
    origin,
    originFeature: request.originFeature,
    originSubFeature: request.originSubFeature,
    friendRequestSenderID: request.owner.id,
  });

  const confirmed = result === 'accepted';
  if (confirmed) {
    await StateObserver.invoke.acceptFriendshipRequest(request);

    // Make the new friend visible to UI that reads GCInstant.friends right
    // after this flow (e.g. the launch gifts popup).
    await refreshExternalFriends();

    track('FriendAdd', {
      friendCount: getBuruFriendsCount(user()),
      method: getFriendshipRequestMethod(request.method),
      originFeature: request.originFeature,
      originSubFeature: request.originSubFeature,
      friendRequestReceiverID: user().id,
      friendRequestSenderID: request.owner.id,
      isReceiver: true,
    });
  }

  return confirmed;
}

/**
 * Reject a friend request
 */
export async function rejectFriendRequest(
  request: FriendshipRequest,
  origin: 'launch' | 'hud',
) {
  track('FriendConfirmPopupOpen', {
    origin,
    originFeature: request.originFeature,
    originSubFeature: request.originSubFeature,
    friendRequestSenderID: request.owner.id,
  });

  // Ask user for confirmation
  const result = await openPopupPromise('popupFriendsConfirmation', {
    player: request.owner,
    prompt: 'reject',
  });

  track('FriendConfirmAction', {
    action: result,
    prompt: 'reject',
    senderID: request.owner.id,
    friendCount: getBuruFriendsCount(user()),
    origin,
    originFeature: request.originFeature,
    originSubFeature: request.originSubFeature,
    friendRequestSenderID: request.owner.id,
  });

  const confirmed = result === 'accepted';
  if (confirmed) {
    await StateObserver.invoke.rejectFriendshipRequest(request);
  }

  return confirmed;
}

export async function unfriend(friendId: string) {
  const details = await withLoading(
    () => fetchFullUserProfile(friendId).get(),
    {
      feature: Feature.FRIENDS._,
      subFeature: Feature.FRIENDS.USER_PROFILE,
    },
  );

  // Profile is null only when the player does not exist
  if (!details) {
    openPopupPromise('popupError', {
      message: i18n(`match2.team.errors.tryAgain`),
    });
    return false;
  }

  // Show unfriend confirmation popup
  const success = await openPopupPromise('popupUnfriendConfirmation', {
    details,
  });

  track('FriendRemoveAction', {
    action: success ? 'removed' : 'kept',
    friendID: friendId,
  });

  // Trigger unfriend action if user confirmed
  if (success) {
    await StateObserver.invoke.unfriend(friendId);

    track('FriendRemove', {
      friendID: friendId,
      friendCount: getBuruFriendsCount(user()),
    });

    // Invalidate friend list cache
    await fetchFriendList().invalidate();
  }

  return success;
}

/**
 * Open the friends popup in selected tab
 */
export async function openFriendsPopup(args: {
  tab: 'search' | 'requests';
  originFeature?: 'menu';
}) {
  const origin = args.originFeature === 'menu' ? 'hud' : undefined;

  // Ensure user will create their public profile before accessing the feature
  if (!user().publicProfile) {
    await openProfile(origin);
  }

  if (!user().tutorial.addFriends) {
    await openPopupPromise('popupFriendsTutorial', {
      origin,
      additionalTracking: { originFeature: args.originFeature },
    });
    await StateObserver.invoke.completeTutorialStep('addFriends');
  }

  await openPopupPromise('popupFriends', {
    tab: args.tab,
    origin,
    additionalTracking: { originFeature: args.originFeature },
  });
}

/**
 * True when the receiver entered the game by tapping the friend-request OA.
 */
export function isEnteringFromFriendRequest(): boolean {
  return (
    GCInstant.entryData?.feature === Feature.FRIENDS._ &&
    GCInstant.entryData?.$subFeature === Feature.FRIENDS.REQUEST
  );
}

/**
 * Launch handler for the in-game friend-request OA. Opens the confirmation
 * popup with `origin: 'launch'` and routes through the standard accept flow
 * so analytics propagate the mirrored origin attribution end-to-end. Bails
 * silently if the request no longer exists in state (already handled on
 * another device) or if the entry payload doesn't carry a sender.
 */
export async function friendRequestLaunchSequence() {
  if (!isEnteringFromFriendRequest()) return;

  // `webReferrer` / `playerID` are not carried on chatbot OA taps (webReferrer
  // is the LIFF redirect URL, not a player ID) — kept as fallbacks so any
  // in-flight messages sent before the payload included `friendRequestSenderID`
  // bail out cleanly via the request lookup below rather than crashing.
  const senderId =
    GCInstant.entryData?.friendRequestSenderID ??
    GCInstant.entryData?.webReferrer ??
    GCInstant.entryData?.playerID;
  if (!senderId) return;

  const request = user().friendshipRequests[senderId] as
    | FriendshipRequest
    | undefined;
  if (!request || request.status !== 'pending') return;

  await acceptFriendRequest(request, 'launch');
}

/**
 * Open the friend requests popup for Add Friends V2 users
 * Simplified popup without tabs or search functionality
 */
export async function openFriendRequestsPopup(args: {
  originFeature?: 'menu';
}) {
  const origin = args.originFeature === 'menu' ? 'hud' : undefined;

  // Ensure user will create their public profile before accessing the feature
  if (!user().publicProfile) {
    await openProfile(origin);
  }

  if (!user().tutorial.addFriends) {
    await openPopupPromise('popupFriendsTutorial', {
      origin,
      additionalTracking: { originFeature: args.originFeature },
    });
    await StateObserver.invoke.completeTutorialStep('addFriends');
  }

  await openPopupPromise('popupFriendRequests', {
    origin,
    additionalTracking: { originFeature: args.originFeature },
  });
}
